import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, delay, map, throwError } from 'rxjs';
import { 
  FreezeReasonsResponse, 
  CreateFreezeRequest, 
  CreateFreezeRequestOnly, 
  FreezeListResponse, 
  FreezeRequestListResponse,
  ApproveFreezeRequest,
  RejectFreezeRequest,
  RequireMoreInfoRequest,
  CancelFreezeRequest,
  UpdateStaffComments,
  FreezeRequestDetails,
  CompFreezeRequest,
  VerifyDocumentRequest,
  RejectDocumentRequest,
  FreezeReason,
  FreezeChargeBreakupRequest,
  CalculateFreezeAdjustmentRequest,
  FreezeCycleAdjustmentResponse,
  RescheduleFreezeRequest
} from '../models/freeze-reason.model';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/models';;
import { environment } from '@fitness-force/environments';
import type { PlanBreakUpCalculationResponse } from '@fitness-force/models';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';


@Injectable({
  providedIn: 'root'
})
export class FreezeReasonService {
  private http = inject(HttpClient);


  private baseUrl = environment.BASE_URL;

  /**
   * Get list of freeze reasons for a tenant
   * @param tenantId - The tenant ID
   * @returns Observable of freeze reasons response
   */
  getFreezeReasons(tenantId: number): Observable<FreezeReason[]> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.GET_FREEZE_REASONS
      .replace('{tenant_id}', tenantId.toString());
    return this.http.get(url).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Create a freeze with billing (for staff with freeze approval rights)
   * @param tenantId - The tenant ID
   * @param clientId - The client ID
   * @param subscriptionId - The subscription ID
   * @param request - The freeze creation request
   * @returns Observable of the created freeze
   */
  createFreeze(tenantId: number, clientId: number, subscriptionId: number, request: CreateFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.CREATE_FREEZE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{subscription_id}', subscriptionId.toString());
    return this.http.post(url, request).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Create a freeze request (for staff without freeze approval rights)
   * @param tenantId - The tenant ID
   * @param clientId - The client ID
   * @param subscriptionId - The subscription ID
   * @param request - The freeze request creation request
   * @returns Observable of the created freeze request
   */
  createFreezeRequest(tenantId: number, clientId: number, subscriptionId: number, request: CreateFreezeRequestOnly): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.CREATE_FREEZE_REQUEST
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{subscription_id}', subscriptionId.toString());
    return this.http.post(url, request).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }


  /**
   * Get list of freeze requests by status (using new API endpoint)
   * @param tenantId - The tenant ID
   * @param page - Page number
   * @param limit - Items per page
   * @param status - Filter by status (optional)
   * @returns Observable of freeze requests response
   */
  getFreezeRequests(tenantId: number, page: number = 1, limit: number = 20, status?: string): Observable<FreezeRequestListResponse> {
    let url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.GET_FREEZE_REQUESTS
      .replace('{tenant_id}', tenantId.toString());
    // if (status) {
    //   url += `&freez_status=${status}`;
    // }
    return this.http.get<FreezeRequestListResponse>(url).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    )
  }

  /**
   * Get list of freezes (using new API endpoint)
   * @param tenantId - The tenant ID
   * @param page - Page number
   * @param limit - Items per page
   * @param freezeType - Filter by freeze type (optional)
   * @returns Observable of freeze list response
   */
  getFreezeList(tenantId: number, page: number = 1, limit: number = 20, freezeType?: string): Observable<FreezeListResponse> {
    let queryString = `?PageNumber=${page}&PageSize=${limit}`;
    if (freezeType) {
      queryString += `&freeze_type=${freezeType}`;
    }
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.GET_FREEZE_LIST
      .replace('{tenant_id}', tenantId.toString()) + queryString;
    return this.http.get<FreezeListResponse>(url).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }
  

  /**
   * Get list of open freeze requests (not completed) - Legacy method
   * @param tenantId - The tenant ID
   * @param page - Page number
   * @param limit - Items per page
   * @returns Observable of freeze requests response
   */
  getOpenFreezeRequests(tenantId: number, page: number = 1, limit: number = 20): Observable<FreezeRequestListResponse> {
    return this.getFreezeRequests(tenantId, page, limit, 'PENDING_APPROVAL,APPROVED');
  }
  
  /**
   * Get list of open freeze requests with filters
   * @param tenantId - The tenant ID
   * @param filters - Filter parameters (freeze_status, from_date, to_date, client_id, subscription_id, page_number, page_size)
   * @returns Observable of freeze request list response
   */
  getOpenFreezeRequestsWithFilters(tenantId: number, filters: any): Observable<FreezeRequestListResponse> {
    const url = `${this.baseUrl}/tenants/${tenantId}/freeze_request`;
    
    // Build query parameters
    const params: any = {};
    
    if (filters.freeze_status) {
      params.freeze_status = filters.freeze_status;
    }
    if (filters.from_date) {
      params.from_date = filters.from_date;
    }
    if (filters.to_date) {
      params.to_date = filters.to_date;
    }
    if (filters.client_id) {
      params.client_id = filters.client_id;
    }
    if (filters.subscription_id) {
      params.subscription_id = filters.subscription_id;
    }
    if (filters.freeze_type) {
      params.freeze_type = filters.freeze_type;
    }
    if (filters.page_number) {
      params.page_number = filters.page_number;
    }
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    return this.http.get<FreezeRequestListResponse>(url, { params }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Get list of completed/scheduled freeze requests - Legacy method
   * @param tenantId - The tenant ID
   * @param page - Page number
   * @param limit - Items per page
   * @param freezeType - Filter by freeze type (optional)
   * @returns Observable of freeze list response
   */
  getCompletedFreezeRequests(tenantId: number, page: number = 1, limit: number = 20, freezeType?: string): Observable<FreezeListResponse> {
    return this.getFreezeList(tenantId, page, limit, freezeType);
  }

  // B2B API Methods based on Freeze V3.3 document

  /**
   * Get freeze request by ID
   * GET /tenants/{tenant_id}/freeze_request/{frz_req_id}
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @returns Observable of freeze request details
   */
  getFreezeRequestById(tenantId: number, freezeRequestId: number): Observable<FreezeRequestDetails> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.GET_FREEZE_REQUEST_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.get<FreezeRequestDetails>(url).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }


  /**
   * Update staff comments for freeze request
   * POST /tenants/{tenant_id}/freeze_request/{frz_req_id}/staff_comments
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @param payload - Staff comments payload
   * @returns Observable of response
   */
  updateStaffComments(tenantId: number, freezeRequestId: number, payload: UpdateStaffComments): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.UPDATE_STAFF_COMMENTS
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Approve freeze request
   * POST /tenants/{tenant_id}/freeze_request/{frz_req_id}/approve
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @param payload - Approve freeze request payload
   * @returns Observable of response
   */
  approveFreezeRequest(tenantId: number, freezeRequestId: number, payload: ApproveFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.APPROVE_FREEZE_REQUEST
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Reject freeze request
   * POST /tenants/{tenant_id}/freeze_request/{frz_req_id}/reject
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @param payload - Reject freeze request payload
   * @returns Observable of response
   */
  rejectFreezeRequest(tenantId: number, freezeRequestId: number, payload: RejectFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.REJECT_FREEZE_REQUEST
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Require more info for freeze request
   * POST /tenants/{tenant_id}/freeze_request/{frz_req_id}/require_more_info
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @param payload - Require more info payload
   * @returns Observable of response
   */
  requireMoreInfo(tenantId: number, freezeRequestId: number, payload: RequireMoreInfoRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.REQUIRE_MORE_INFO
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }
  /**
   * Cancel freeze request
   * POST /tenants/{tenant_id}/freeze_request/{frz_req_id}/cancel
   * @param tenantId - The tenant ID
   * @param freezeRequestId - The freeze request ID
   * @param payload - Cancel freeze request payload
   * @returns Observable of response
   */
  cancelFreezeRequest(tenantId: number, freezeRequestId: number, payload: CancelFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.CANCEL_FREEZE_REQUEST
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_request_id}', freezeRequestId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Comp a freeze so it's not counted in quota
   * POST /tenants/{tenant_id}/freeze/{freeze_id}/comp
   * @param tenantId - The tenant ID
   * @param freezeId - The freeze ID
   * @param payload - Comp freeze payload
   * @returns Observable of response
   */
  compFreeze(tenantId: number, freezeId: number, payload: CompFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.COMP_FREEZE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{freeze_id}', freezeId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Verify client document
   * POST /tenants/{tenant_id}/documents/{clt_doc_id}/verify
   * @param tenantId - The tenant ID
   * @param documentId - The document ID
   * @param payload - Verify document payload
   * @returns Observable of response
   */
  verifyDocument(tenantId: number, documentId: number, payload: VerifyDocumentRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.VERIFY_DOCUMENT
      .replace('{tenant_id}', tenantId.toString())
      .replace('{document_id}', documentId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
    
  }

  /**
   * Reject client document
   * POST /tenants/{tenant_id}/documents/{clt_doc_id}/reject
   * @param tenantId - The tenant ID
   * @param documentId - The document ID
   * @param payload - Reject document payload
   * @returns Observable of response
   */
  rejectDocument(tenantId: number, documentId: number, payload: RejectDocumentRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.REJECT_DOCUMENT
      .replace('{tenant_id}', tenantId.toString())
      .replace('{document_id}', documentId.toString());
    return this.http.post(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Get freeze charge breakup calculation
   * POST /tenants/{tenant_id}/charges/freeze_breakup_calculation
   * @param tenantId - The tenant ID
   * @param subscriptionId - The subscription ID
   * @param payload - Calculation payload
   * @returns Observable of calculation response
   */
  getFreezeChargeBreakup(tenantId: number, subscriptionId: number, payload: FreezeChargeBreakupRequest): Observable<PlanBreakUpCalculationResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.GET_FREEZE_CHARGE_BREAKUP
      .replace('{tenant_id}', tenantId.toString())
      .replace('{subscription_id}', subscriptionId.toString());
    return this.http.post<PlanBreakUpCalculationResponse>(url, payload).pipe(
      map((res: any) => {
        return res
      }),
    catchError((error: Response) => {
      if (error.status === 404) {
        return throwError(() => new NotFoundError(error));
      } else if (error.status === 400) {
        return throwError(() => new BadRequestError(error))
      }
      return throwError(() => new AppErrors(error));
    })
    );
  }

  /**
   * Calculate freeze cycle adjustment and future cycle adjustments
   * POST /tenants/{tenant_id}/clients/{client_id}/subscriptions/{clt_subs_id}/calculate_freeze_adjust
   * 
   * This API calculates billing cycle adjustments based on the adjustment_type:
   * - "FullFreeze": Calculate adjustments for a new freeze
   * - "FreezeReschedule": Calculate adjustments when rescheduling an existing freeze
   * - "FreezeStop": Calculate adjustments when stopping a freeze early
   * 
   * @param tenantId - The tenant ID
   * @param clientId - The client ID
   * @param subscriptionId - The subscription ID
   * @param payload - Calculation payload with start/end dates and adjustment type
   * @returns Observable of freeze cycle adjustment response
   */
  calculateFreezeAdjustment(tenantId: number, clientId: number, subscriptionId: number, payload: CalculateFreezeAdjustmentRequest): Observable<FreezeCycleAdjustmentResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.CALCULATE_FREEZE_ADJUSTMENT
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{clt_subs_id}', subscriptionId.toString());
    return this.http.post<FreezeCycleAdjustmentResponse>(url, payload).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Reschedule freeze
   * PUT /tenants/{tenant_id}/clients/{client_id}/subscriptions/{clt_subs_id}/freeze/{freeze_id}/reschedule
   * @param tenantId - The tenant ID
   * @param clientId - The client ID
   * @param subscriptionId - The subscription ID
   * @param freezeId - The freeze ID
   * @param payload - Reschedule freeze request payload
   * @returns Observable of response
   */
  rescheduleFreeze(tenantId: number, clientId: number, subscriptionId: number, freezeId: number, payload: RescheduleFreezeRequest): Observable<any> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.FREEZE_MANAGEMENT.RESCHEDULE_FREEZE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{clt_subs_id}', subscriptionId.toString())
      .replace('{freeze_id}', freezeId.toString());
    return this.http.put(url, payload).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }


}
